export interface IntentionDetail {
  id: string
  name: string
  slug: string | null
  description: string
  isSaved: boolean
  ritualCount: number
  practitionerCount: number
}

export interface IntentionRitual {
  id: string
  title: string
  ritualType: string
  isPaid: boolean
  fee: number | null
  feeCurrency: string | null
  duration: string
  durationType: string
  city: string | null
  state: string | null
  ritualTs: string | null
  coverImage: string | null
  user: {
    id: string
    username: string | null
    name: string | null
    profilePictureUrl: string | null
  } | null
  isSaved: boolean
}

export interface IntentionFiltersState {
  search: string
  sort: "trending" | "newest"
  price: "all" | "free" | "paid"
  format: "all" | "IN_PERSON" | "VIRTUAL_COURSE" | "SELF_GUIDED" | "SELF_GUIDED_COURSE"
  duration: "all" | "short" | "medium" | "long"
  content?: string
  traditions?: string
  spiritualTradition?: string
  city?: string
  state?: string
  country?: string
}

export interface IntentionRitualsResponse {
  rituals: IntentionRitual[]
  total: number
  page: number
  totalPages: number
}

export const RITUAL_FORMAT_LABELS: Record<string, string> = {
  all: "Format",
  IN_PERSON: "In-Person",
  VIRTUAL_COURSE: "Live Virtual",
  SELF_GUIDED: "Self-Guided",
  SELF_GUIDED_COURSE: "Self-Guided Course",
}

// ── Tab types ──────────────────────────────────────────

export type IntentionTab =
  | "rituals"
  | "sessions"
  | "community"
  | "members"
  | "experts"
  | "ritualists"

export const INTENTION_TABS: { key: IntentionTab; label: string }[] = [
  { key: "rituals", label: "Rituals" },
  { key: "sessions", label: "Sessions" },
  { key: "community", label: "Community" },
  { key: "ritualists", label: "Ritualists" },
  { key: "members", label: "Members" },
  { key: "experts", label: "Experts" },
]

export interface CommonIntentionFilters {
  sort: string
  search?: string
  spiritualTradition: string
}

// ── Session types ──────────────────────────────────────

export interface IntentionSession {
  id: string
  title: string
  format: string
  duration: number
  participationFee: string
  price: number | null
  coverImage: string | null
  tags?: Array<{ id: string; tagText: string }>
  creator?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    followers?: number
  } | null
  isLive: boolean
}

export interface IntentionSessionsResponse {
  sessions: IntentionSession[]
  total: number
  page: number
  totalPages: number
}

export interface IntentionSessionsFilters extends CommonIntentionFilters {
  duration: string
}

// ── Community types ────────────────────────────────────

export interface IntentionCommunity {
  id: string
  name: string
  slug: string | null
  description: string | null
  coverImage: string | null
  createdAt: string
  memberCount?: number
  ritualCount?: number
  isSaved?: boolean
}

export interface IntentionCommunitiesResponse {
  communities: IntentionCommunity[]
  total: number
  page: number
  totalPages: number
}

// ── User types (Members, Experts, Ritualists) ──────────

export interface IntentionUser {
  id: string
  name: string | null
  username: string | null
  profilePictureUrl: string | null
  city: string | null
  state: string | null
  bio: string | null
  followerCount: number
  tags: string[]
  isFollowing: boolean
  sessionCount?: number
  ritualCount?: number
  metricCount?: number
  metricLabel?: string
}

export interface IntentionUsersResponse {
  members?: IntentionUser[]
  experts?: IntentionUser[]
  ritualists?: IntentionUser[]
  total: number
  page: number
  totalPages: number
}
